import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import AdminTopbar from '../../components/AdminTopbar';
import { TeamAPI, UploadAPI } from '../../lib/api'; // ✅ Added UploadAPI
import { useConfirm } from '../../contexts/ConfirmContext';
import { useNotification } from '../../contexts/NotificationContext';
import LoadingState from '../components/LoadingState';
import Spinner from '../../components/Spinner';

export default function TeamEdit(){
  const confirm = useConfirm();
  const notify = useNotification();
  const { id } = useParams();
  const isNew = id === 'new' || !id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: '', role: '', bio: '', avatar: '', socials: { linkedin:'', twitter:'', github:'', website:'' }
  });
  const [avatarOk, setAvatarOk] = useState(true);

  // ============================================================================
  // ✅ FIXED: MODERN IMAGE UPLOAD FUNCTION (USES UploadAPI)
  // ============================================================================
  
  async function onAvatarFileChange(e){
    const file = e.target.files?.[0];
    if(!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      notify.error('Only image files are allowed (jpeg, jpg, png, gif, webp)');
      return;
    }
    
    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      notify.error('File size must be less than 5MB');
      return;
    }
    
    setUploading(true);
    
    try {
      // ✅ Use UploadAPI instead of axios
      const { data } = await UploadAPI.uploadSingle(file);
      
      if (data && data.url) {
        // Update form with new image URL
        setForm(f => ({ ...f, avatar: data.url }));
        setAvatarOk(true);
        notify.success('Avatar uploaded successfully!');
      } else {
        throw new Error('Upload failed - no URL returned');
      }
      
    } catch (err) {
      console.error('Upload error:', err);
      notify.error(err.message || 'Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  }

  useEffect(()=>{
    if (isNew) return;
    let mounted = true;
    (async()=>{
      setLoading(true);
      try{
        const { member } = await TeamAPI.get(id);
        if(!mounted) return;
        setForm({
          name: member.name || '',
          role: member.role || '',
          bio: member.bio || '',
          avatar: member.avatar || '',
          socials: {
            linkedin: member.socials?.linkedin || '',
            twitter: member.socials?.twitter || '',
            github: member.socials?.github || '',
            website: member.socials?.website || '',
          }
        });
      }catch(err){ if(mounted) notify.error(err.message||'Failed to load member'); }
      finally{ if(mounted) setLoading(false); }
    })();
    return ()=>{ mounted=false };
  },[id, isNew]);

  async function handleSave(e){
    e?.preventDefault?.();
    setSaving(true);
    try{
      const payload = { ...form };
      if (isNew){ await TeamAPI.create(payload); notify.success('Member created'); navigate('/admin/team'); }
      else { await TeamAPI.update(id, payload); notify.success('Member saved'); }
    }catch(err){ 
      notify.error(err.message||'Failed to save'); 
    }
    finally{ setSaving(false); }
  }

  async function handleDelete(){
    if (isNew) return;
    await confirm.show({
      title: 'Remove Member',
      message: 'Remove this member?',
      variant: 'danger',
      confirmText: 'Remove',
      action: async () => {
        await TeamAPI.remove(id); 
        notify.success('Member removed');
        navigate('/admin/team'); 
      }
    });
  }

  if (loading){
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <main className="admin-content create-post">
          <AdminTopbar />
          <LoadingState message="Loading member details..." />
        </main>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-content create-post">
        <AdminTopbar />

        <div className="page-bar sticky">
          <div className="breadcrumbs" aria-label="Breadcrumbs">
            <Link to="/admin">Dashboard</Link>
            <span>/</span>
            <Link to="/admin/team">Team</Link>
            <span>/</span>
            <strong className="active" aria-current="page">{isNew ? 'Create' : 'Edit'}</strong>
          </div>
        </div>

        <h1 className="page-title" style={{marginTop:'.25rem'}}>{isNew? 'Add Member' : 'Edit Member'}</h1>

        <form onSubmit={handleSave} style={{marginTop:'1rem'}}>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
            <div className="stack" style={{display:'grid',gap:'1rem'}}>
              <section className="section-card">
                <h3>Basic details</h3>
                <div className="form-grid" style={{marginTop:'.75rem'}}>
                  <label className="form-label">Name</label>
                  <input className="form-field ux-input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g., Jane Doe" required />
                </div>
                <div className="form-grid" style={{marginTop:'.75rem'}}>
                  <label className="form-label">Role</label>
                  <input className="form-field ux-input" value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))} placeholder="e.g., Senior Designer" />
                </div>
                <div className="form-grid" style={{marginTop:'.75rem'}}>
                  <label className="form-label">Bio</label>
                  <textarea className="form-field ux-input" rows={5} value={form.bio} onChange={e=>setForm(f=>({...f,bio:e.target.value}))} placeholder="A short intro about this member" />
                </div>
              </section>
            </div>
            <aside className="section-card" aria-label="Sidebar settings">
              <h3>Profile</h3>
              <div className="form-grid" style={{marginTop:'.5rem'}}>
                <label className="form-label">Avatar URL</label>
                <div style={{display:'flex',gap:'.5rem',alignItems:'center'}}>
                  <input 
                    className="form-field ux-input" 
                    value={form.avatar} 
                    onChange={e=>{ setAvatarOk(true); setForm(f=>({...f,avatar:e.target.value})); }} 
                    placeholder="https://..." 
                    style={{flex:1}} 
                  />
                  <input 
                    id="avatar-file" 
                    type="file" 
                    accept="image/*" 
                    onChange={onAvatarFileChange} 
                    style={{display:'none'}} 
                  />
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={()=>document.getElementById('avatar-file').click()}
                    disabled={uploading}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                  >
                    {uploading && <Spinner size="sm" />}
                    {uploading ? 'Uploading' : 'Upload'}
                  </button>
                </div>
                {(form.avatar || form.name) && (
                  <div className="preview" style={{marginTop:'.6rem',display:'flex',alignItems:'center',gap:'.6rem'}}>
                    <div className="avatar-preview">
                      {form.avatar && avatarOk ? (
                        <img 
                          src={form.avatar} 
                          alt="avatar preview" 
                          onError={()=>setAvatarOk(false)} 
                          style={{width:56,height:56,borderRadius:'50%',objectFit:'cover'}} 
                        />
                      ) : (
                        <div className="avatar-fallback" style={{width:56,height:56,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',background:'#eef2f7',color:'#0f172a',fontWeight:800}}>
                          {(form.name||'').split(' ').map(n=>n[0]).slice(0,2).join('').toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div>
                      <small className="muted">Preview</small>
                      {!/^https?:\/\/.*\.(png|jpe?g|webp|gif)(\?.*)?$/i.test(form.avatar||'') && form.avatar && (
                        <div className="hint" style={{marginTop:'.15rem'}}>Use a direct image URL ending in .png, .jpg, .jpeg, .webp, or .gif</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="divider" style={{margin:'1rem 0'}}/>
              <h4 style={{margin:'0 0 .4rem'}}>Social links</h4>
              <div className="form-grid">
                <label className="form-label">LinkedIn</label>
                <input className="form-field ux-input" value={form.socials.linkedin} onChange={e=>setForm(f=>({...f,socials:{...f.socials, linkedin:e.target.value}}))} placeholder="https://linkedin.com/in/..." />
              </div>
              <div className="form-grid" style={{marginTop:'.6rem'}}>
                <label className="form-label">Twitter/X</label>
                <input className="form-field ux-input" value={form.socials.twitter} onChange={e=>setForm(f=>({...f,socials:{...f.socials, twitter:e.target.value}}))} placeholder="https://x.com/..." />
              </div>
              <div className="form-grid" style={{marginTop:'.6rem'}}>
                <label className="form-label">GitHub</label>
                <input className="form-field ux-input" value={form.socials.github} onChange={e=>setForm(f=>({...f,socials:{...f.socials, github:e.target.value}}))} placeholder="https://github.com/..." />
              </div>
              <div className="form-grid" style={{marginTop:'.6rem'}}>
                <label className="form-label">Website</label>
                <input className="form-field ux-input" value={form.socials.website} onChange={e=>setForm(f=>({...f,socials:{...f.socials, website:e.target.value}}))} placeholder="https://..." />
              </div>
            </aside>
          </div>

          <div className="admin-sticky-footer">
            <button type="button" className="px-4 py-2 rounded-lg border border-gray-700 hover:bg-gray-800 transition-colors text-sm text-gray-300" onClick={()=>navigate('/admin/team')}>Cancel</button>
            {!isNew && <button type="button" className="px-4 py-2 rounded-lg border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors text-sm" onClick={handleDelete}>Delete</button>}
            <button type="submit" className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-colors text-sm font-medium" disabled={saving || uploading} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', opacity: (saving || uploading) ? 0.7 : 1 }}>
              {saving && <Spinner size="sm" />}
              <span>{saving? 'Saving...':'Save'}</span>
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}